import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import './pilotage.css';

type Stats = {
  ca: number;
  returningClients: number;
  totalClients: number;
  avgNps: number | null;
  syncedAgo: string;
};

function formatMoney(n: number) {
  return `${n.toLocaleString('fr-FR')} FCFA`;
}

export function MarketingPilotageHub() {
  const { profile } = useAuth();
  const [stats, setStats] = useState<Stats>({
    ca: 0,
    returningClients: 0,
    totalClients: 0,
    avgNps: null,
    syncedAgo: '—',
  });

  useEffect(() => {
    if (!profile) return;
    void (async () => {
      const [leads, customers] = await Promise.all([
        supabase.from('crm_leads').select('budget, status, source, updated_at').eq('user_id', profile.id),
        supabase
          .from('crm_customers')
          .select('purchase_count, nps_score, updated_at')
          .eq('user_id', profile.id),
      ]);

      const won = (leads.data ?? []).filter((l) => l.status === 'gagne');
      const ca = won.reduce((sum, l) => sum + Number(l.budget ?? 0), 0);

      const list = customers.data ?? [];
      const returning = list.filter((c) => Number(c.purchase_count ?? 0) > 1).length;
      const npsValues = list.map((c) => c.nps_score).filter((v): v is number => typeof v === 'number');
      const avgNps = npsValues.length
        ? Math.round((npsValues.reduce((a, b) => a + b, 0) / npsValues.length) * 10) / 10
        : null;

      const dates = [...(leads.data ?? []), ...list]
        .map((r) => r.updated_at)
        .filter(Boolean)
        .map((d) => new Date(d as string).getTime());
      let syncedAgo = 'Synchronisé à l’instant';
      if (dates.length) {
        const mins = Math.max(0, Math.round((Date.now() - Math.max(...dates)) / 60000));
        syncedAgo = mins < 1 ? 'Synchronisé à l’instant' : `Synchronisé il y a ${mins} min`;
      }

      setStats({
        ca,
        returningClients: returning,
        totalClients: list.length,
        avgNps,
        syncedAgo,
      });
    })();
  }, [profile]);

  return (
    <div className="pilotage-mkt">
      <div className="pilotage-mkt-header">
        <div>
          <h3>Pilotage Marketing</h3>
          <p>CRM, performances, marketplace et assistant IA.</p>
        </div>
      </div>

      <Link to="/app/marketplace-crm/crm" className="pilotage-mkt-crm">
        <div>
          <div className="pilotage-mkt-crm-title">
            <strong>Mon CRM Intégré</strong>
            <span className="pilotage-mkt-badge">Pipeline</span>
            <span className="pilotage-mkt-badge muted">Salesforce / Hubspot</span>
          </div>
          <p className="pilotage-mkt-crm-sub">PIPELINE & PRÉVISIONS EN TEMPS RÉEL</p>
          <p className="muted" style={{ margin: 0 }}>
            {stats.syncedAgo}
          </p>
        </div>
        <span className="btn btn-primary">Ouvrir mon CRM →</span>
      </Link>

      <div className="pilotage-mkt-grid">
        <Link to="/app/marketplace-crm/crm" className="pilotage-mkt-card">
          <div className="pilotage-mkt-icon teal">📈</div>
          <h4>Analyse du CA</h4>
          <p className="muted">Revenus et croissance</p>
          <strong className="pilotage-mkt-metric">{formatMoney(stats.ca)}</strong>
          <span className="muted">Opportunités gagnées (budget CRM)</span>
        </Link>

        <Link to="/app/marketplace-crm/fidelisation" className="pilotage-mkt-card">
          <div className="pilotage-mkt-icon rose">♥</div>
          <h4>Fidélisation</h4>
          <p className="muted">Satisfaction et NPS</p>
          <strong className="pilotage-mkt-metric">
            {stats.returningClients} / {stats.totalClients}
          </strong>
          <span className="muted">
            Clients revenus
            {stats.avgNps != null ? ` · NPS moyen ${stats.avgNps}` : ''}
          </span>
        </Link>

        <Link to="/app/marketplace-crm/marketplace" className="pilotage-mkt-card pilotage-mkt-card--market">
          <div className="pilotage-mkt-icon blue">🏪</div>
          <h4>Marketplace</h4>
          <p className="muted">Générez de nouveaux leads dès l’activation du module</p>
          <span className="btn btn-primary">Gérer mes boutiques</span>
        </Link>

        <Link to="/app/marketplace-crm/assistant-marketing" className="pilotage-mkt-card pilotage-mkt-card--ai">
          <div className="pilotage-mkt-icon navy">🤖</div>
          <h4>Assistant IA Marketing</h4>
          <div className="pilotage-mkt-chips">
            <span>Analyse Marché</span>
            <span>Persona Clients</span>
            <span>Benchmark</span>
            <span>Optimisation</span>
          </div>
          <span className="btn btn-ghost">Lancer un diagnostic complet</span>
        </Link>
      </div>
    </div>
  );
}
